<template>
	<div class="claim">
		<PictureComp
			class="claim__image"
			:image-url="backdropImage"
			image-profile="backdrop"
			alt="claim"
			:lazy="false"
			fetchpriority="high"
		/>

		<div class="claim__text">
			<h1 class="claim__text__heading">{{ $t('SPORTS_WEBAPP_JW_SPORT_TITLE') }}</h1>
			<i18n-t tag="h2" keypath="SPORTS_WEBAPP_JW_SPORT_SUBTITLE" class="claim__text__subheading">
				<template #br><br /></template>
			</i18n-t>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { thumborFilter, ThumborOptions } from '@/filters/thumbor';
import PictureComp from '@/components/picture/PictureComp.vue';
import { AppDirection } from '@/enums/app-direction';
import { storeToRefs } from 'pinia';
import { useMetaStore } from '@/stores/pinia/useMetaStore';

const { appDirection } = storeToRefs(useMetaStore());

const isRTL = computed(() => appDirection.value === AppDirection.RTL);
const backdropImage = computed(() => {
	return thumborFilter()({
		url: isRTL.value
			? `/${ASSETS_DIR}/img/sports/bg-all-sports-1920-rtl.png`
			: `/${ASSETS_DIR}/img/sports/bg-all-sports-1920-v2.png`,
		imageType: 'backdrop',
		extension: '.webp',
	} as ThumborOptions);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/sports.scss';
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins.scss';

.claim {
	position: relative;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr;
	place-items: center;

	&__text {
		grid-area: 1 / 1 / 2 / 2;
		z-index: 10;

		@media (max-width: 550px) {
			text-align: center;
		}
		@media (min-width: 550px) {
			justify-self: self-start;
		}

		@media (max-width: 768px) {
			padding-inline: 24px;
		}
		@media (min-width: 768px) {
			padding-inline: 15px;
		}
		@media (min-width: 1050px) {
			padding-inline: 64px;
		}

		&__heading {
			color: $color-brand-default;

			font-style: normal;
			font-weight: 900;
			font-size: 32px;
			line-height: 32px;

			margin-bottom: 32px;

			@media (max-width: 550px) {
				padding: 0 10px;
			}

			@media (min-width: 550px) {
				font-size: 68px;
				line-height: 68px;
				letter-spacing: unset;
			}

			@media (max-width: 1050px) {
				font-size: 46px;
				line-height: 46px;
				margin-bottom: 24px;
			}
		}

		&__subheading {
			font-size: 20px;
			font-weight: 400;
			margin: 0;
			color: $color-primary-light;
			white-space: pre-line;

			@media (max-width: 550px) {
				padding: 0 10px;
			}
		}
	}

	&__image {
		grid-area: 1 / 1 / 2 / 2;
		:deep(.picture-comp__img) {
			object-fit: cover;
			height: 40vh;
		}
	}

	&::after {
		content: '';
		position: absolute;
		height: 100%;
		width: 100%;
		top: 1px;
		z-index: 0;

		background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(6, 13, 23, 1) 100%);
	}
}
</style>
